import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const NAVIGATION_ACTION_SAVE_ANIMATION_MIN_MS = 600;
export const NAVIGATION_ACTION_OUTCOME_VISIBLE_MS = 1400;

export type NavigationActionVisualState = 'idle' | 'saving' | 'saved' | 'error';
export type NavigationActionContentPosition = 'active' | 'next' | 'previous';
type NavigationActionSaveOutcome = 'saved' | 'error' | null;

interface NavigationActionFeedbackOptions {
  disabled?: boolean;
  isSaving?: boolean;
  saveFailed?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown;
}

interface NavigationActionContentLabels {
  errorLabel: ReactNode;
  idleContent: ReactNode;
  savedLabel: ReactNode;
  savingLabel: ReactNode;
}

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  Boolean(value) && typeof (value as { then?: unknown }).then === 'function';

// skipPromise when save opens a sync confirm dialog (no Promise to track).
export const invokeNavigationSave = (
  handler: (() => void | Promise<void>) | null | undefined,
  skipPromise = false
): void | Promise<void> => {
  if (!handler) {
    return undefined;
  }

  const result = handler();

  if (skipPromise) {
    if (isPromiseLike(result)) {
      // The caller opted out of outcome tracking; the handler owns user feedback.
      Promise.resolve(result).catch(() => undefined);
    }
    return undefined;
  }

  return result;
};

export const getNavigationActionContentPosition = (
  visualState: NavigationActionVisualState,
  contentState: NavigationActionVisualState
): NavigationActionContentPosition => {
  if (visualState === contentState) {
    return 'active';
  }

  if (
    (visualState === 'idle' && contentState === 'saving') ||
    (visualState === 'saving' && (contentState === 'saved' || contentState === 'error')) ||
    (visualState === 'saved' && contentState === 'idle') ||
    (visualState === 'error' && contentState === 'idle')
  ) {
    return 'next';
  }

  return 'previous';
};

export const useNavigationActionContentWidth = (
  visualState: NavigationActionVisualState,
  { errorLabel, idleContent, savedLabel, savingLabel }: NavigationActionContentLabels
) => {
  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const idleContentRef = useRef<HTMLSpanElement | null>(null);
  const savingContentRef = useRef<HTMLSpanElement | null>(null);
  const savedContentRef = useRef<HTMLSpanElement | null>(null);
  const errorContentRef = useRef<HTMLSpanElement | null>(null);

  // Keep the control width aligned to the active save-state label during transitions.
  useLayoutEffect(() => {
    const activeContentRef = {
      error: errorContentRef,
      idle: idleContentRef,
      saved: savedContentRef,
      saving: savingContentRef,
    }[visualState];
    const nextWidth = activeContentRef.current?.offsetWidth;

    if (!nextWidth) {
      return;
    }

    setContentWidth(nextWidth);
  }, [errorLabel, idleContent, savedLabel, savingLabel, visualState]);

  return {
    contentWidth,
    errorContentRef,
    idleContentRef,
    savedContentRef,
    savingContentRef,
  };
};

export const useNavigationActionFeedback = ({
  disabled,
  isSaving = false,
  saveFailed = false,
  onClick,
}: NavigationActionFeedbackOptions) => {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [visualState, setVisualState] = useState<NavigationActionVisualState>('idle');
  const autoSavingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const outcomeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const pendingOutcomeRef = useRef<NavigationActionSaveOutcome>(null);
  const wasSavingRef = useRef(false);
  const isSavingVisible = isSaving || isAutoSaving;
  const isActionInProgress = isSavingVisible || visualState !== 'idle';
  const isActionDisabled = disabled || isActionInProgress;

  const clearAutoSavingTimeout = useCallback(() => {
    if (!autoSavingTimeoutRef.current) {
      return;
    }

    clearTimeout(autoSavingTimeoutRef.current);
    autoSavingTimeoutRef.current = null;
  }, []);

  const clearOutcomeTimeout = useCallback(() => {
    if (!outcomeTimeoutRef.current) {
      return;
    }

    clearTimeout(outcomeTimeoutRef.current);
    outcomeTimeoutRef.current = null;
  }, []);

  const finishAutoSaving = useCallback(
    (startedAt: number, outcome: Exclude<NavigationActionSaveOutcome, null>) => {
      if (!isMountedRef.current) {
        return;
      }

      clearAutoSavingTimeout();
      const elapsedMs = Date.now() - startedAt;
      const remainingMs = Math.max(0, NAVIGATION_ACTION_SAVE_ANIMATION_MIN_MS - elapsedMs);

      autoSavingTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) {
          autoSavingTimeoutRef.current = null;
          return;
        }

        pendingOutcomeRef.current = outcome;
        setIsAutoSaving(false);
        autoSavingTimeoutRef.current = null;
      }, remainingMs);
    },
    [clearAutoSavingTimeout]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!onClick || isActionDisabled) {
        return;
      }

      const startedAt = Date.now();
      const result = onClick(event);

      if (!isPromiseLike(result)) {
        return;
      }

      clearAutoSavingTimeout();
      pendingOutcomeRef.current = null;
      setIsAutoSaving(true);
      Promise.resolve(result).then(
        () => finishAutoSaving(startedAt, 'saved'),
        () => finishAutoSaving(startedAt, 'error')
      );
    },
    [clearAutoSavingTimeout, finishAutoSaving, isActionDisabled, onClick]
  );

  // Mirror external or auto-detected saving into the animated visual state.
  useEffect(() => {
    if (isSavingVisible) {
      clearOutcomeTimeout();
      setVisualState('saving');
      wasSavingRef.current = true;
      return;
    }

    if (!wasSavingRef.current) {
      return;
    }

    wasSavingRef.current = false;
    const outcome = saveFailed ? 'error' : pendingOutcomeRef.current;
    pendingOutcomeRef.current = null;

    if (outcome !== 'saved' && outcome !== 'error') {
      setVisualState('idle');
      return;
    }

    setVisualState(outcome);
    outcomeTimeoutRef.current = setTimeout(() => {
      setVisualState('idle');
      outcomeTimeoutRef.current = null;
    }, NAVIGATION_ACTION_OUTCOME_VISIBLE_MS);
  }, [clearOutcomeTimeout, isSavingVisible, saveFailed]);

  // Prevent delayed save/outcome timers from updating state after unmount.
  useEffect(
    () => () => {
      isMountedRef.current = false;
      clearAutoSavingTimeout();
      clearOutcomeTimeout();
    },
    [clearAutoSavingTimeout, clearOutcomeTimeout]
  );

  return {
    handleClick,
    isActionDisabled,
    visualState,
  };
};
